<template>
  <!-- "Keep this" (recipient journey R5.9, R5.10): a quiet text link in the
       room for a guest place. Never a gate, never a popup: it opens in place,
       asks one thing (her email), and nothing in the room waits on it. -->
  <div class="kt" data-testid="keep-this">
    <button v-if="step === 'link'" type="button" class="kt-link" data-testid="keep-this-link" @click="open">
      {{ KEEP_LINK }}
    </button>
    <form v-else-if="step === 'ask'" class="kt-ask" novalidate @submit.prevent="send">
      <label class="kt-label" for="kt-email">{{ KEEP_ASK }}</label>
      <div class="kt-row">
        <input
          id="kt-email" ref="input" v-model="email" type="email" inputmode="email" autocomplete="email"
          autocapitalize="off" spellcheck="false" class="kt-input" data-testid="keep-this-email"
          placeholder="you@example.com"
        />
        <button type="submit" class="kt-send" data-testid="keep-this-send" :disabled="busy || !looksLikeEmail(email)">
          {{ busy ? '…' : 'Send' }}
        </button>
      </div>
      <p v-if="suggestion" class="kt-hint" data-testid="keep-this-suggestion">
        Did you mean <button type="button" class="kt-fix" @click="email = suggestion">{{ suggestion }}</button>?
      </p>
      <p v-if="error" class="kt-error" role="alert">{{ error }}</p>
      <button type="button" class="kt-later" @click="step = 'link'">Not now</button>
    </form>
    <template v-else>
      <p class="kt-sent" data-testid="keep-this-sent">{{ KEEP_SENT }}</p>
      <!-- A wrong address costs nothing: she can give another (the newest wins, R5.13). -->
      <button type="button" class="kt-link kt-again" data-testid="keep-this-again" @click="open">Another address</button>
    </template>
  </div>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue'
import { store } from '@/store'
import { careKeep } from '@/api-service'
import { KEEP_ASK, KEEP_LINK, KEEP_SENT, looksLikeEmail, suggestEmail } from '@/utils/stage-two'

const emit = defineEmits(['sent'])
const step = ref('link')
const email = ref('')
const busy = ref(false)
const error = ref('')
const input = ref(null)
const suggestion = computed(() => suggestEmail(email.value))

async function open () {
  step.value = 'ask'
  error.value = ''
  await nextTick()
  if (input.value) input.value.focus()
}

async function send () {
  if (busy.value || !looksLikeEmail(email.value)) return
  busy.value = true
  error.value = ''
  try {
    await careKeep(store.session_id, email.value.trim())
    step.value = 'sent'
    emit('sent')
  } catch (e) {
    const data = (e && e.response && e.response.data) || {}
    error.value = data.reason === 'rate_limited' ? 'Too many for that address. Try again in an hour.'
      : data.reason === 'invalid_contact' ? 'That does not look like an email address.'
        : 'That did not go through. Try again.'
  } finally {
    busy.value = false
  }
}
</script>

<style scoped>
.kt { text-align: center; padding: 6px 0 2px; }
.kt-link, .kt-later, .kt-fix {
  background: none; border: 0; padding: 4px 2px; font: inherit; cursor: pointer;
  color: #b9a6dd; text-decoration: underline; text-underline-offset: 3px;
}
.kt-link { font-size: 13.5px; min-height: 32px; }
.kt-link:hover, .kt-later:hover, .kt-fix:hover { color: #FFDFA3; }
.kt-link:focus-visible, .kt-later:focus-visible, .kt-fix:focus-visible, .kt-send:focus-visible {
  outline: 2px solid #FFDFA3; outline-offset: 2px;
}
.kt-ask { display: flex; flex-direction: column; gap: 8px; max-width: 420px; margin: 0 auto; text-align: left; }
.kt-label { color: #e9dcff; font-size: 13.5px; }
.kt-row { display: flex; gap: 8px; }
.kt-input {
  flex: 1 1 auto; min-width: 0; box-sizing: border-box; min-height: 44px;
  background: rgba(20, 7, 31, 0.6); color: #e9dcff;
  border: 1px solid rgba(155, 134, 201, 0.4); border-radius: 12px; padding: 10px 12px;
  font: inherit; font-size: 16px;
}
.kt-send {
  background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #FFDFA3;
  border: 1px solid rgba(240, 217, 163, 0.55); border-radius: 999px;
  padding: 10px 20px; min-height: 44px; font: inherit; font-size: 13.5px; font-weight: 700; cursor: pointer;
}
.kt-send:disabled { opacity: 0.55; cursor: default; }
.kt-hint, .kt-error, .kt-sent { margin: 0; font-size: 13px; }
.kt-hint { color: #d8c8f0; }
.kt-fix { padding: 0; font-size: 13px; }
.kt-error { color: #ffb4a8; }
.kt-later { align-self: flex-end; font-size: 12.5px; }
.kt-sent { color: #e9dcff; text-align: center; }
.kt-again { font-size: 12.5px; }
</style>
